import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import { SkeletonLine } from 'components/Skeleton'
import VerifiedRoundedIcon       from '@mui/icons-material/VerifiedRounded'
import AssignmentRoundedIcon     from '@mui/icons-material/AssignmentRounded'
import BuildRoundedIcon          from '@mui/icons-material/BuildRounded'
import TuneRoundedIcon           from '@mui/icons-material/TuneRounded'
import InventoryRoundedIcon      from '@mui/icons-material/InventoryRounded'
import FactCheckRoundedIcon      from '@mui/icons-material/FactCheckRounded'
import GavelRoundedIcon          from '@mui/icons-material/GavelRounded'
import RateReviewRoundedIcon     from '@mui/icons-material/RateReviewRounded'
import OpenInFullRoundedIcon     from '@mui/icons-material/OpenInFullRounded'
import { useSelector } from 'react-redux'
import { selectFeatureVisibility, selectFeatureDefaultOwner } from 'features/auth/ducks/selectors'
import { isFeatureVisible } from 'constants/featureGroups'
import { fetchBp } from 'services/BpService'
import { getProgramme, bulkApproveEvidence } from 'services/checklistService'
import { setComplianceTracking } from 'services/ProgrammeService'
import { notifyComplianceChanged, useComplianceChanged, useRefetchOnFocus } from 'utils/complianceEvents'
import Breadcrumb from 'features/navigation/Breadcrumb'
import TabNav from 'components/TabNav'
import ComplianceScoreExplainer from 'features/compliance/ComplianceScoreExplainer'
import { scoreColour } from 'features/dashboard/charts/scoreColour'
import SharedBackLink from 'components/BackLink'

/**
 * Unified container for the compliance flow on a single Building Passport.
 * Wraps both the existing "Overview" (CompliancePage body) and the new
 * "Programme" (ProgrammePage body) in one persistent shell:
 *
 *   Dashboard › <BP name> › Compliance / Programme
 *
 * Children render INSIDE the shell — they don't own their own header,
 * back button, or breadcrumb. This is the SAP Fiori "object page" pattern:
 * one identity per object (the BP), persistent context, tabs for sections.
 */
const TABS = [
  { key: 'overview',  label: 'Overview',           Icon: VerifiedRoundedIcon },
  { key: 'programme', label: 'Compliance tracker', Icon: AssignmentRoundedIcon },
  { key: 'legislation', label: 'By legislation',   Icon: GavelRoundedIcon },
  { key: 'remedials', label: 'Remedials',          Icon: BuildRoundedIcon },
  { key: 'setup',     label: 'Obligation list',    Icon: TuneRoundedIcon },
  { key: 'assets',    label: 'Asset register',     Icon: InventoryRoundedIcon },
  { key: 'permits',   label: 'Permits',            Icon: FactCheckRoundedIcon },
]

const ComplianceShellChrome = ({ activeTab, score, headerActions, children, bpId: bpIdProp, embedded = false, onTabChange }) => {
  const params = useParams()
  const bpId = bpIdProp ?? params.bpId
  const navigate = useNavigate()
  const location = useLocation()
  const [bp, setBp] = useState(null)
  const [totals, setTotals] = useState(null)
  const [canDecide, setCanDecide] = useState(true)
  const [canManage, setCanManage] = useState(true)
  const [approving, setApproving] = useState(false)
  const [tracksCompliance, setTracksCompliance] = useState(null)
  const [togglingTracking, setTogglingTracking] = useState(false)
  const featureVisibility = useSelector(selectFeatureVisibility)
  const featureOwner = useSelector(selectFeatureDefaultOwner)

  const loadTotals = useCallback(() => getProgramme(bpId)
    .then(res => {
      setTotals(res.data?.data?.totals || null)
      setCanDecide(!!res.data?.data?.canDecideCompliance)
      setCanManage(!!res.data?.data?.canManageObligations)
      setTracksCompliance(res.data?.data?.tracksCompliance !== false)
    })
    .catch(() => { setTotals(null) }), [bpId])

  const handleSetTracking = useCallback((enabled) => {
    setTogglingTracking(true)
    setComplianceTracking(bpId, enabled)
      .then(() => { notifyComplianceChanged(); return loadTotals() })
      .then(() => toast.success(enabled
        ? 'Compliance tracking turned on for this building.'
        : 'Compliance tracking turned off for this building. You can turn it back on here any time.'))
      .catch(() => toast.error('Could not update compliance tracking. Please try again.'))
      .finally(() => setTogglingTracking(false))
  }, [bpId, loadTotals])

  useEffect(() => { loadTotals() }, [loadTotals])
  useComplianceChanged(loadTotals)
  useRefetchOnFocus(loadTotals)

  useEffect(() => {
    let cancelled = false
    fetchBp(bpId)
      .then(res => { if (!cancelled) setBp(res.data?.data || null) })
      .catch(() => { /* swallow. Shell renders without name */ })
    return () => { cancelled = true }
  }, [bpId])

  const dashboardReturnUrl = `/dashboard?altitude=bp&entityId=${bpId}`

  const goBack = () => navigate(`/bp-list/${bpId}`)

  const breadcrumb = useMemo(() => {
    const tabLabel = activeTab === 'programme' ? 'Compliance tracker'
                   : activeTab === 'legislation' ? 'By legislation'
                   : activeTab === 'remedials' ? 'Remedials'
                   : activeTab === 'setup'     ? 'Obligation list'
                   : activeTab === 'assets'    ? 'Asset register'
                   : activeTab === 'permits'   ? 'Permits to work'
                   : 'Compliance overview'
    return [
      { type: 'dashboard', label: 'Dashboard', to: dashboardReturnUrl },
      { type: 'bp',        label: bp?.name || 'Building Passport', to: `/bp-list/${bpId}` },
      { type: activeTab,   label: tabLabel },
    ]
  }, [activeTab, bp, bpId, dashboardReturnUrl])

  const goTab = key => {
    if (key === activeTab && !embedded) return
    if (onTabChange) return onTabChange(key)
    const target = key === 'programme' ? `/bp-list/${bpId}/programme`
                 : key === 'legislation' ? `/bp-list/${bpId}/compliance/legislation`
                 : key === 'remedials' ? `/bp-list/${bpId}/remedials`
                 : key === 'setup'     ? `/bp-list/${bpId}/compliance/setup`
                 : key === 'assets'    ? `/bp-list/${bpId}/compliance/assets`
                 : key === 'permits'   ? `/bp-list/${bpId}/compliance/permits`
                 : `/bp-list/${bpId}/compliance`
    navigate(`${target}${location.search || ''}`)
  }

  const inReview = totals?.inReview || 0
  const approvable = totals?.approvable || 0
  const needsAction = Math.max(0, inReview - approvable)
  const ready = totals?.readiness ? totals.readiness.ready : true
  const reviewNeeded = canDecide && inReview > 0
  const onReviewTab = activeTab === 'overview' || activeTab === 'programme'
  const showReview = reviewNeeded && onReviewTab
  const showSetup = canManage && !reviewNeeded && !!totals?.readiness && !ready
    && activeTab !== 'programme' && activeTab !== 'setup'

  const approveAll = () => {
    setApproving(true)
    bulkApproveEvidence(bpId)
      .then(res => {
        const n = res.data?.data?.approved || 0
        toast.success(n > 0
          ? `Approved ${n} matched ${n === 1 ? 'document' : 'documents'}. Your compliance score has updated.`
          : 'No matched documents were ready to approve.')
        notifyComplianceChanged()
        return loadTotals()
      })
      .then(() => navigate(`/bp-list/${bpId}/programme${location.search || ''}`))
      .catch(() => toast.error('Could not approve the evidence. Please try again.'))
      .finally(() => setApproving(false))
  }

  const nested = embedded && !!onTabChange

  const trackingControl = canDecide && tracksCompliance !== null && (
          <TrackingControl>
            <TrackingLabel>Compliance tracking</TrackingLabel>
            <Switch
              type='button'
              role='switch'
              aria-checked={tracksCompliance}
              aria-label='Compliance tracking'
              $on={tracksCompliance}
              disabled={togglingTracking}
              onClick={() => handleSetTracking(!tracksCompliance)}
              title={tracksCompliance
                ? 'Compliance tracking is on. Turn it off to stop setup reminders and the daily digest and hide the score. Reversible any time.'
                : 'Compliance tracking is off. Turn it back on to resume the score, reminders and digest.'}
            >
              <SwitchKnob $on={tracksCompliance} />
            </Switch>
          </TrackingControl>
  )

  const goReview = () => navigate(`/bp-list/${bpId}/compliance${location.search || ''}#review`)

  return (
    <Wrap $embedded={embedded}>
      <ShellHeader $embedded={embedded}>
        <ShellTop>
          {!embedded && (
            <>
              <BackPill onClick={goBack} title={bp?.name ? `Back to ${bp.name}` : 'Back to this Building Passport'}>
                Back
              </BackPill>
              <Breadcrumb items={breadcrumb} compact />
            </>
          )}
          {embedded && !onTabChange && (
            <WorkspaceLink onClick={() => navigate(`/bp-list/${bpId}/compliance${location.search || ''}`)} title='Open the full compliance workspace for this Building Passport'>
              Open full compliance workspace
              <OpenInFullRoundedIcon style={{ fontSize: 14 }} />
            </WorkspaceLink>
          )}
          <Spacer />
          {!nested && trackingControl}
          {!nested && headerActions}
        </ShellTop>

        {!embedded && (
          <Identity>
            <BpName
              $clickable={!!bp?.name}
              onClick={bp?.name ? goBack : undefined}
              title={bp?.name ? 'Back to Building Passport details' : undefined}
            >
              {bp?.name || <SkeletonLine $w='140px' $h={16} />}
            </BpName>
            <BpMeta>
              {bp?.address?.fullAddress && <MetaChip>{bp.address.fullAddress}</MetaChip>}
              {bp?.status && <MetaChip $tone='ok'>{bp.status}</MetaChip>}
            </BpMeta>
            {tracksCompliance === false ? (
              <ScorePill $tint='#94a3b8' title='Compliance tracking is off for this building'>
                <ScorePillHead>
                  <ScorePillLabel>Compliance</ScorePillLabel>
                </ScorePillHead>
                <NotTrackedValue>Not tracked</NotTrackedValue>
              </ScorePill>
            ) : (score !== undefined && score !== null && (
              <ScorePill $tint={tintForScore(score)}>
                <ScorePillHead>
                  <ScorePillLabel>Compliance</ScorePillLabel>
                  <ComplianceScoreExplainer align='right' />
                </ScorePillHead>
                <ScorePillValue $tint={tintForScore(score)}>{Math.round(score)}%</ScorePillValue>
              </ScorePill>
            ))}
          </Identity>
        )}

        {embedded && !nested && tracksCompliance === false && (
          <EmbeddedIdentity>
            <ScorePill $tint='#94a3b8' title='Compliance tracking is off for this building'>
              <ScorePillHead>
                <ScorePillLabel>Compliance</ScorePillLabel>
              </ScorePillHead>
              <NotTrackedValue>Not tracked</NotTrackedValue>
            </ScorePill>
          </EmbeddedIdentity>
        )}

        {embedded && !nested && tracksCompliance !== false && score !== undefined && score !== null && (
          <EmbeddedIdentity>
            <ScorePill $tint={tintForScore(score)}>
              <ScorePillHead>
                <ScorePillLabel>Compliance</ScorePillLabel>
                <ComplianceScoreExplainer align='right' />
              </ScorePillHead>
              <ScorePillValue $tint={tintForScore(score)}>{Math.round(score)}%</ScorePillValue>
            </ScorePill>
          </EmbeddedIdentity>
        )}

        {nested ? (
          <NestedBar>
            <NestedTabs>
              <TabNav
                ariaLabel='Compliance sections'
                activeKey={activeTab}
                onSelect={goTab}
                size='sm'
                bordered={false}
                items={TABS.filter(t => {
            if (t.key === 'setup') return canManage
            if (t.key === 'permits') return isFeatureVisible(featureVisibility, 'permits', featureOwner)
            if (t.key === 'remedials') return isFeatureVisible(featureVisibility, 'tasks', featureOwner)
            return true
          }).map(t => ({ key: t.key, label: t.label, icon: t.Icon }))}
              />
            </NestedTabs>
            <NestedActions>
              {trackingControl}
              {headerActions}
            </NestedActions>
          </NestedBar>
        ) : (
          <TabNav
            ariaLabel='Compliance sections'
            activeKey={activeTab}
            onSelect={goTab}
            items={TABS.filter(t => {
            if (t.key === 'setup') return canManage
            if (t.key === 'permits') return isFeatureVisible(featureVisibility, 'permits', featureOwner)
            if (t.key === 'remedials') return isFeatureVisible(featureVisibility, 'tasks', featureOwner)
            return true
          }).map(t => ({ key: t.key, label: t.label, icon: t.Icon }))}
          />
        )}
      </ShellHeader>

      <ShellBody $embedded={embedded}>
        {showReview && (
          <Prompt $tone='review'>
            <PromptIcon $tone='review'><RateReviewRoundedIcon style={{ fontSize: 20 }} /></PromptIcon>
            <PromptText>
              {approvable > 0 ? (
                <>
                  <PromptTitle>
                    {approvable} matched {approvable === 1 ? 'document is' : 'documents are'} ready to approve
                  </PromptTitle>
                  <PromptBody>
                    Matched evidence doesn’t count towards your compliance score until you approve it.
                    {needsAction > 0 && ` ${needsAction} more ${needsAction === 1 ? 'has' : 'have'} matched evidence but still need a date added or are tracked per flat. Review them in the tracker.`}
                  </PromptBody>
                </>
              ) : (
                <>
                  <PromptTitle>
                    {inReview} matched {inReview === 1 ? 'document' : 'documents'} can’t be approved yet
                  </PromptTitle>
                  <PromptBody>
                    These have matched evidence but need a date added (for periodic checks) or are tracked per flat. Open the tracker to add the details and approve them there.
                  </PromptBody>
                </>
              )}
            </PromptText>
            <PromptActions>
              {approvable > 0 && (
                <PromptPrimary $tone='review' onClick={approveAll} disabled={approving}>
                  {approving ? 'Approving…' : `Approve ${approvable} matched`}
                </PromptPrimary>
              )}
              <PromptGhost onClick={goReview}>{approvable > 0 ? 'Review individually →' : 'Open tracker →'}</PromptGhost>
            </PromptActions>
          </Prompt>
        )}
        {showSetup && (
          <Prompt $tone='setup'>
            <PromptIcon $tone='setup'><TuneRoundedIcon style={{ fontSize: 20 }} /></PromptIcon>
            <PromptText>
              <PromptTitle>Review this building's obligation list</PromptTitle>
              <PromptBody>Confirm which obligations apply and review matched evidence to unlock its compliance score.</PromptBody>
            </PromptText>
            <PromptActions>
              <PromptPrimary $tone='setup' onClick={() => goTab('setup')}>Open obligation list →</PromptPrimary>
            </PromptActions>
          </Prompt>
        )}
        {children}
      </ShellBody>
    </Wrap>
  )
}

const ComplianceShell = ({ chromeless = false, children, ...props }) => (
  chromeless ? <>{children}</> : <ComplianceShellChrome {...props}>{children}</ComplianceShellChrome>
)

export default ComplianceShell

const tintForScore = scoreColour

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Wrap = styled.div`
  background: ${({ $embedded, theme }) => $embedded ? 'transparent' : theme.colors.lighterGrey};
  min-height: ${({ $embedded }) => $embedded ? 'auto' : '100vh'};
`

const ShellHeader = styled.header`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid #ececec;
  padding: ${({ $embedded }) => $embedded ? '0 0 0' : '14px 32px 0'};
  @media (max-width: 768px) { padding: ${({ $embedded }) => $embedded ? '0' : '12px 16px 0'}; }
  animation: ${fadeIn} 320ms ease both;
`

const ShellTop = styled.div`
  display: flex; align-items: center; gap: 12px;
  flex-wrap: wrap;
`

const BackPill = styled(SharedBackLink)``

const Spacer = styled.div` flex: 1; `
const TrackingControl = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
`
const TrackingLabel = styled.span`
  font-size: 12px; font-weight: 600; color: #64748b; white-space: nowrap;
`
const Switch = styled.button`
  position: relative; width: 40px; height: 22px; flex-shrink: 0;
  border: none; border-radius: 999px; padding: 0; cursor: pointer;
  background: ${({ $on }) => ($on ? '#33becc' : '#cbd5e1')};
  transition: background 0.18s ease;
  &:disabled { opacity: 0.6; cursor: default; }
`
const SwitchKnob = styled.span`
  position: absolute; top: 2px; left: 2px; width: 18px; height: 18px;
  border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.18s ease;
  transform: translateX(${({ $on }) => ($on ? '18px' : '0')});
`
const NotTrackedValue = styled.div`
  font-size: 15px; font-weight: 700; color: #64748b; line-height: 1.1; white-space: nowrap;
`

const WorkspaceLink = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px;
  background: rgba(51, 190, 204, 0.10);
  border: 1px solid rgba(51, 190, 204, 0.28);
  color: #1f8c97;
  border-radius: 999px;
  font-size: 12px; font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 160ms ease;
  &:hover {
    background: rgba(51, 190, 204, 0.18);
    border-color: rgba(51, 190, 204, 0.45);
  }
`

const EmbeddedIdentity = styled.div`
  display: flex; align-items: center; justify-content: flex-end;
  padding: 4px 0 12px;
`

const Identity = styled.div`
  display: flex; align-items: center; gap: 14px;
  padding: 14px 0 16px;
  flex-wrap: wrap;
`

const BpName = styled.h1`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 30px;
  letter-spacing: -0.8px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.1;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: color 160ms ease;
  &:hover { color: ${({ $clickable, theme }) => ($clickable ? theme.colors.secondary : theme.colors.haiti)}; }
`

const BpMeta = styled.div`
  display: inline-flex; gap: 6px; flex-wrap: wrap;
`

const MetaChip = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $tone }) => $tone === 'ok' ? 'rgba(51,190,204,0.10)' : '#f2f2f4'};
  color: ${({ $tone, theme }) => $tone === 'ok' ? '#1f8c97' : theme.colors.grey[800]};
  border: 1px solid ${({ $tone }) => $tone === 'ok' ? 'rgba(51,190,204,0.22)' : '#e4e4e7'};
  text-transform: ${({ $tone }) => $tone === 'ok' ? 'uppercase' : 'none'};
`

const ScorePill = styled.div`
  margin-left: auto;
  display: inline-flex; flex-direction: column;
  align-items: flex-end;
  padding: 9px 16px;
  background: ${({ $tint }) => `${$tint}0d`};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  border-radius: 12px;
`

const ScorePillHead = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
`

const ScorePillLabel = styled.span`
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
`

const ScorePillValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 26px;
  letter-spacing: -0.6px;
  line-height: 1;
  color: ${({ $tint, theme }) => $tint || theme.colors.haiti};
  margin-top: 3px;
  font-variant-numeric: tabular-nums;
`

const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin: 10px 0 14px;
  padding: 4px;
  background: #f1f2f4;
  border-radius: 12px;
  max-width: 100%;
  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar { display: none; }
    & > button { flex-shrink: 0; }
  }
`

const Tab = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  border: none;
  border-radius: 9px;
  padding: 8px 13px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 12.5px;
  letter-spacing: 0.01em;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  color: ${({ $active, theme }) => ($active ? theme.colors.haiti : theme.colors.grey[800])};
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(12,18,41,0.12)' : 'none')};
  transition: background-color 160ms ease, color 160ms ease, box-shadow 160ms ease;
  svg { opacity: ${({ $active }) => ($active ? 1 : 0.55)}; transition: opacity 160ms ease; }
  &:hover {
    color: ${({ theme }) => theme.colors.haiti};
    background: ${({ $active }) => ($active ? '#fff' : 'rgba(255,255,255,0.55)')};
    svg { opacity: 1; }
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 1px;
  }
`


const ShellBody = styled.main`
  padding: ${({ $embedded }) => $embedded ? '20px 0 8px' : '24px 32px 48px'};
  @media (max-width: 768px) { padding: ${({ $embedded }) => $embedded ? '16px 0 8px' : '16px 12px 40px'}; }
  max-width: ${({ $embedded }) => $embedded ? 'none' : '1280px'};
  margin: 0 auto;
`

const Prompt = styled.div`
  display: flex; align-items: center; gap: 14px;
  padding: 14px 16px;
  margin-bottom: 20px;
  border-radius: 12px;
  border: 1px solid ${({ $tone }) => $tone === 'review' ? '#fcd34d' : '#ececec'};
  background: ${({ $tone }) => $tone === 'review' ? '#fffbeb' : '#fff'};
  animation: ${fadeIn} 280ms ease both;
  @media (max-width: 768px) { flex-wrap: wrap; }
`

const PromptIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; flex-shrink: 0;
  border-radius: 9px;
  color: ${({ $tone }) => $tone === 'review' ? '#d97706' : '#1f8c97'};
  background: ${({ $tone }) => $tone === 'review' ? '#fef3c7' : 'rgba(51,190,204,0.10)'};
`

const PromptText = styled.div` flex: 1; min-width: 200px; `

const PromptTitle = styled.div`
  font-size: 14px; font-weight: 800;
  letter-spacing: -0.1px;
  color: ${({ theme }) => theme.colors.haiti};
`

const PromptBody = styled.div`
  font-size: 12.5px; margin-top: 2px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const PromptActions = styled.div`
  display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0;
  @media (max-width: 768px) { width: 100%; margin-top: 4px; }
`

const PromptPrimary = styled.button`
  font: inherit; cursor: pointer;
  border: none; border-radius: 999px;
  padding: 9px 16px;
  font-size: 12.5px; font-weight: 800; letter-spacing: 0.02em;
  color: #fff;
  background: ${({ $tone }) => $tone === 'setup' ? '#1f8c97' : '#d97706'};
  transition: background 160ms ease;
  &:hover { background: ${({ $tone }) => $tone === 'setup' ? '#19727b' : '#855207'}; }
  &:disabled { opacity: 0.6; cursor: default; }
`

const PromptGhost = styled.button`
  font: inherit; cursor: pointer;
  border: 1px solid #e4cf8f; border-radius: 999px;
  padding: 8px 14px;
  font-size: 12.5px; font-weight: 700;
  background: transparent; color: #d97706;
  transition: background 160ms ease;
  &:hover { background: #fef3c7; }
`

const NestedBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  border-bottom: 1px solid rgba(12, 18, 41, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    border-bottom: none;
  }
`

const NestedTabs = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    border-bottom: 1px solid rgba(12, 18, 41, 0.08);
  }
`

const NestedActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding-bottom: 6px;

  @media (max-width: 768px) {
    padding-bottom: 2px;
    flex-wrap: wrap;
  }
`
